import React from 'react';
import ReactDOM from 'react-dom';
import useFetch from 'use-http';
import { useNavigate } from 'react-router-dom';
import Paginate from 'components/UI/Paginate';
import usePaginate from 'hooks/usePaginate';
import Table, { TableFilter } from 'components/UI/Table';
import Input from 'components/UI/Input';
import Button from 'components/UI/Button';
import { IStores } from 'types/stores';
import { useUnit } from 'effector-react';
import { $store } from 'models/store';
import { GroupsEnum } from 'types/users';
import { $user } from 'models/user';
import DatePicker from 'react-datepicker';
import FileDownload from 'assets/img/svg/download.svg';
import moment from 'moment';

// Компоненты вынесены за пределы основного компонента
type ButtonProps = React.HTMLProps<HTMLButtonElement>;
const StoreExampleCustomInput = React.forwardRef<HTMLButtonElement, ButtonProps>(function StoreDatePickerInput(
	{ value, onClick },
	ref,
) {
	return <Button onClick={onClick}>{value}</Button>;
});

// Компонент фильтра дат для магазинов
const StoreDateFilter = ({
	filter,
	setFilter,
	hasFullAccess,
}: {
	filter: TableFilter;
	setFilter: (filter: TableFilter) => void;
	hasFullAccess: boolean;
}) => {
	const retailDaysLimit = parseInt(process.env.REACT_APP_RETAIL_DAYS_LIMIT || '3', 10);
	const minAllowedDate = !hasFullAccess ? moment().subtract(retailDaysLimit, 'days').toDate() : null;

	const [startDate, setStartDate] = React.useState(() => {
		return filter?.fromDate || new Date();
	});
	const [endDate, setEndDate] = React.useState(() => {
		return filter?.toDate || new Date();
	});

	const onChange = (dates: any) => {
		const [start, end] = dates;
		setStartDate(start);
		setEndDate(end);
		setFilter({ ...filter, fromDate: start, toDate: end });
	};

	return (
		<DatePicker
			selected={startDate}
			onChange={onChange}
			startDate={startDate}
			endDate={endDate}
			customInput={<StoreExampleCustomInput />}
			selectsRange
			minDate={!hasFullAccess ? minAllowedDate : undefined}
			popperContainer={({ children }) => {
				const portal = document.getElementById('root') || document.body;
				return ReactDOM.createPortal(children, portal);
			}}
		/>
	);
};

// Функция для создания фильтра дат для магазинов
const createStoreDateFilterFunction = (
	filter: TableFilter,
	setFilter: (filter: TableFilter) => void,
	hasFullAccess: boolean,
) => () => <StoreDateFilter filter={filter} setFilter={setFilter} hasFullAccess={hasFullAccess} />;

const perPage = 10;

const host = process.env.REACT_APP_BACKEND_URL;

export default function Stores() {
	const { get, loading, error } = useFetch();
	const { get: getXLSX } = useFetch(host, { responseType: 'blob' });
	const store = useUnit($store);
	const user = useUnit($user);

	const retailDaysLimit = parseInt(process.env.REACT_APP_RETAIL_DAYS_LIMIT || '3', 10);
	const hasFullAccess = user?.groups?.includes(GroupsEnum.FULL_ACCESS);

	const [data, setData] = React.useState([]);
	const [sortField, setSortField] = React.useState('number');
	const [sortDirection, setSortDirection] = React.useState('ASC');
	const [filter, setFilter] = React.useState<TableFilter>(() => {
		const toDate = new Date();
		const fromDate = new Date();

		// С 1-го числа месяца для пользователей с полным доступом
		if (hasFullAccess) {
			fromDate.setDate(1);
		} else {
			// Последние 3 дня для остальных пользователей
			fromDate.setDate(fromDate.getDate() - retailDaysLimit);
		}

		return new TableFilter({ fromDate, toDate });
	});
	const { pageCount, offset, setCount, handlePageClick, currentPage } = usePaginate(perPage, {
		sortField,
		sortDirection,
	});

	const load = async (filter = '', currentOffset = offset) => {
		let storeId = null;

		// Администраторы и пользователи с полным доступом имеют доступ ко всем магазинам
		if (user?.groups?.includes(GroupsEnum.ADMIN) || user?.groups?.includes(GroupsEnum.FULL_ACCESS)) {
			storeId = '';
		} else if (store?.id) {
			// Остальные пользователи ограничены своим магазином
			storeId = store.id;
		}

		if (storeId !== null) {
			const query = new URLSearchParams({
				offset: currentOffset.toString(),
				limit: perPage.toString(),
				filter: filter,
				storeId: storeId,
				sortField: sortField,
				sortDirection: sortDirection,
			}).toString();

			const result = await get(`/api/stores?${query}`);

			if (result?.rows) {
				setData(result.rows);
				setCount(result.count);
			}
		}
	};

	const handleSort = (field: string) => {
		let newDirection = 'ASC';
		if (sortField === field) {
			newDirection = sortDirection === 'ASC' ? 'DESC' : 'ASC';
		}

		setSortField(field);
		setSortDirection(newDirection);
		load();
	};

	React.useEffect(() => {
		load();
	}, [offset, perPage, store, user, sortField, sortDirection]);

	const navigate = useNavigate();

	const headers = [
		{
			value: 'number',
			title: (
				<div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
					<span style={{ marginRight: '10px' }}>Номер</span>
					<button onClick={() => handleSort('number')}>
						{(() => {
							if (sortField === 'number') {
								return sortDirection === 'ASC' ? '↓' : '↑';
							}
							return '⇅';
						})()}
					</button>
				</div>
			),
			filter: createStoreDateFilterFunction(filter, setFilter, hasFullAccess ?? false),
		},
		{
			value: 'title',
			title: (
				<div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
					<span style={{ marginRight: '10px' }}>Название</span>
					<button onClick={() => handleSort('title')}>
						{(() => {
							if (sortField === 'title') {
								return sortDirection === 'ASC' ? '↓' : '↑';
							}
							return '⇅';
						})()}
					</button>
				</div>
			),
			filter: React.useCallback(() => {
				console.log('Фильтр', filter);
				const params = new URLSearchParams();

				for (const [key, value] of Object.entries(filter)) {
					if (value !== undefined && value !== null) {
						params.append(key, String(value));
					}
				}

				const hasFullStoreAccess =
					user?.groups?.includes(GroupsEnum.ADMIN) ||
					user?.groups?.includes(GroupsEnum.FULL_ACCESS) ||
					user?.groups?.some(group => group.startsWith('/rd'));

				if (!hasFullStoreAccess && store?.id) {
					params.append('storeId', store.id);
				}

				return (
					<Button
						onClick={() => {
							getXLSX(`/api/session/exportToXls?${params}`)
								.then(response => {
									const url = window.URL.createObjectURL(response);
									const a = document.createElement('a');
									a.href = url;
									a.download = 'session.xlsx';
									document.body.appendChild(a);
									a.click();
									a.remove();
									window.URL.revokeObjectURL(url);
								})
								.catch(error => {
									console.error(error);
								});
						}}
						variant="secondary"
						size="sm"
						endIcon={
							<i>
								<img
									src={FileDownload}
									style={{
										filter: 'invert(22%) sepia(37%) saturate(900%) hue-rotate(193deg) brightness(90%) contrast(83%)',
									}}
								/>
							</i>
						}
					>
						Скачать отчет по статистике присутствия в магазине
					</Button>
				);
			}, [filter, setFilter]),
		},
		{
			value: 'address',
			width: '500px',
			title: (
				<div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
					<span style={{ marginRight: '10px' }}>Адрес</span>
					<button onClick={() => handleSort('address')}>
						{(() => {
							if (sortField === 'address') {
								return sortDirection === 'ASC' ? '↑' : '↓';
							}
							return '⇅';
						})()}
					</button>
				</div>
			),

			filter: React.useCallback(() => {
				const newFilters = { ...filter };

				if (!!user && user?.groups?.includes(GroupsEnum.ADMIN)) {
					// Admin has access to all stores
				} else if (store?.id) {
					newFilters['storeId'] = store.id;
				}

				const params = Object.entries(newFilters)
					.map(item => item.join('='))
					.join('&');

				return (
					<Button
						onClick={() => {
							getXLSX(`/api/breach/reportToXls?${params}`)
								.then(response => {
									const url = window.URL.createObjectURL(response);
									const a = document.createElement('a');
									a.href = url;
									a.download = 'breachs.xlsx';
									document.body.appendChild(a);
									a.click();
									a.remove();
									window.URL.revokeObjectURL(url);
								})
								.catch(error => {
									console.error(error);
								});
						}}
						variant="secondary"
						size="sm"
						endIcon={
							<i>
								<img
									src={FileDownload}
									style={{
										filter: 'invert(22%) sepia(37%) saturate(900%) hue-rotate(193deg) brightness(90%) contrast(83%)',
									}}
								/>
							</i>
						}
					>
						Скачать отчет по нарушениям фотофиксации
					</Button>
				);
			}, [filter, setFilter, store]),
		},
		{ value: 'manager', title: 'Руководитель' },
		{
			value: 'operation',
			title: '',
			width: '300px',
			formatted: React.useCallback(
				(row: IStores) => {
					const routeChange = () => {
						const path = `/admin/stores/${row.id}/sessions`;
						navigate(path);
					};

					return (
						<div className="row">
							<div className="column-xs-6">
								<Button size="sm" variant="secondary" onClick={routeChange}>
									Статистика присутствия
								</Button>
							</div>
						</div>
					);
				},
				[navigate],
			),
		},
	];

	const handleChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
		await load(event.target.value);
	};

	return (
		<div>
			{error && 'Error!'}
			<div className="row">
				{user?.groups?.includes(GroupsEnum.ADMIN) ||
				user?.groups?.includes(GroupsEnum.FULL_ACCESS) ||
				user?.groups?.some(group => group.startsWith('/rd')) ? (
					<div className="column-md-3">
						<Input onChange={handleChange} name="filter" label="Поиск по номеру магазина" />
					</div>
				) : null}
		</div>
		<Table headers={headers} rows={data} isLoading={loading} />
		{pageCount > 1 && <Paginate handlePageClick={handlePageClick} pageCount={pageCount} forcePage={currentPage} />}
		</div>
	);
}
